import { useMutation } from "@tanstack/react-query";
import { useApiQuery, useApiMutation } from "../services/react-query";
import { notifications } from "@mantine/notifications";
import { getAccessToken } from "../services/authStore";

export const useGetAllRegistrations = (params) => {
  return useApiQuery({
    queryOptions: {
      queryKey: ["registrations", params],
      enabled: !!params.eventId,
    },
    fetchOptions: {
      url: "/registrations",
      method: "GET",
      params,
    },
  });
};

export const useGetRegistration = ({ registrationId }) => {
  return useApiQuery({
    queryOptions: { queryKey: ["registrations", registrationId] },
    fetchOptions: {
      url: `/registrations/${registrationId}`,
      method: "GET",
    },
  });
};

export const useGetHistoryLogs = (eventId, params) => {
  return useApiQuery({
    queryOptions: {
      queryKey: ["registrations", "history", eventId, params],
      enabled: !!eventId,
    },
    fetchOptions: {
      url: `/registrations/history`,
      method: "GET",
      params: { eventId, ...params },
    },
  });
};

export const useUpdateRegistrationTickets = () => {
  return useApiMutation({
    fetchOptions: {
      url: `/registrations/tickets`,
      method: "PATCH",
    },
    successMessage: {
      title: "Registration tickets were updated successfully",
      color: "green",
    },
    invalidateQueryKeys: [["registrations"]],
  });
};

export const useCreateRegistration = () => {
  return useApiMutation({
    fetchOptions: {
      url: "/registrations",
      method: "POST",
    },
    invalidateQueryKeys: [["registrations"]],
    hideNotification: true,
  });
};

export const useUpdateRegistration = () => {
  return useApiMutation({
    fetchOptions: {
      url: `/registrations`,
      method: "PATCH",
    },
    successMessage: {
      title: "Registration was updated successfully",
      color: "green",
    },
    invalidateQueryKeys: [["registrations"]],
  });
};

export const useGetRegistrationsOverview = (params) => {
  return useApiQuery({
    queryOptions: {
      queryKey: ["registrations", "overview", params],
      enabled: !!params.eventId,
    },
    fetchOptions: {
      url: "/registrations/overview",
      method: "GET",
      params,
    },
  });
};

export const useGetRegistrationEvent = (params) => {
  return useApiQuery({
    queryOptions: {
      queryKey: ["registrations", "event", params],
      enabled: !!params.eventSlug,
    },
    fetchOptions: {
      url: "/registrations/event",
      method: "GET",
      params,
    },
  });
};

export const useExportRegistrations = () => {
  return useMutation({
    mutationFn: async (data) => {
      const token = await getAccessToken();

      return fetch("/api/registrations/export", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      }).then(async (response) => {
        if (!response.ok) {
          const responseData = await response.json();
          throw new Error(
            responseData.error ||
              "An error occurred while exporting registrations"
          );
        }
        // Extract the filename from the Content-Disposition header
        const contentDisposition = response.headers.get("Content-Disposition");
        const fileName = contentDisposition
          ? contentDisposition.split("filename=")[1].replace(/"/g, "") // Remove quotes if present
          : "registrations.csv"; // Fallback filename

        return { blob: response.blob(), fileName };
      });
    },
    mutationKey: ["exportRegistrations"],
    onSuccess: async ({ blob, fileName }) => {
      const responseBlob = await blob;
      const url = window.URL.createObjectURL(responseBlob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();

      notifications.show({
        title: "Success",
        message: "Registrations exported successfully",
        color: "green",
      });
    },
    onError: (error) => {
      notifications.show({
        title: "Error",
        message:
          error.message || "An error occurred while exporting registrations",
        color: "red",
      });
    },
  });
};

export const useGetRegistrationSettings = (eventId) => {
  return useApiQuery({
    queryOptions: {
      queryKey: ["registrations", "settings", eventId],
      enabled: !!eventId,
    },
    fetchOptions: {
      url: `/registrations-settings`,
      method: "GET",
      params: { eventId },
    },
  });
};

export const useUpdateRegistrationSettings = (eventId) => {
  return useApiMutation({
    fetchOptions: {
      url: `/registrations-settings/${eventId}`,
      method: "PATCH",
    },
    successMessage: {
      title: "Registration settings were updated successfully",
    },
    invalidateQueryKeys: [["registrations", "settings", eventId]],
  });
};
